/**
 * Track 2 Phase 5 — registry-driven `advanceDraftSessionWith{Point,Text}`.
 *
 * The two final monolithic switches in `draftStateMachine.ts`
 * (`advanceDraftSessionWithPoint:146` — 59 cases; and
 * `advanceDraftSessionWithText:1313` — ~53 step branches) follow the same
 * "5 sites must agree" coupling problem the rest of Track 2 already
 * eliminated for prompt / preview / textInputReference. Phase 5 delivers
 * the final two registry-driven dispatchers here.
 *
 * ## Design
 *
 * Per-step callbacks live in `Partial<Record<DraftStep, AdvanceWithPointFn>>`
 * and `Partial<Record<DraftStep, AdvanceWithTextFn>>` lookup tables. Each
 * callback receives the same `(session, point|text, ctx)` triple and
 * returns either `{ nextSession, commit? }` (handled) or `null`
 * (fall-through to remaining shared logic / sentinel).
 *
 * Keys present in this table: only the DraftStep variants for which the
 * legacy switch produced a non-trivial transition. Steps without an entry
 * fall through to the dispatcher's default branch (preserving the legacy
 * `case "...": return { nextSession: session };` no-op or the trailing
 * `default: return { nextSession: session };` fallback).
 *
 * ## CLAUDE.md fidelity
 *
 * Per ПРИОРИТЕТ #1 (нет упрощений) — every case body from the legacy
 * switch is reproduced verbatim. No defaults, no wildcards, no dropped
 * AutoCAD parity branches. The `null` fall-through is the only behavioural
 * shortcut, and only for steps the legacy switch already excluded or
 * routed via early-delegate paths (dimensions / blockAuthoring / modify).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { BoundaryLoop } from "@dxf-ai-editor/drawing-model";
import { findBoundaryFromSeed, isAnonymousBlockName } from "@dxf-ai-editor/drawing-model";
import {
  computeModelExtent,
  distancePointToSegment,
  MIN_REVCLOUD_ARC_CHORD,
  revisionCloudOutlineFromSource,
  type ModelExtent,
} from "@dxf-ai-editor/command-engine";

import { t } from "../../i18n";
import {
  arcSceEndPoint,
  circumscribingCircleFromThree,
  formatNumber,
  formatPoint,
  generatePolygonVertices,
  rectangleCorners,
  resolveArcGeometry,
} from "../draftSharedHelpers";
import type {
  AdvanceResult,
  AdvanceResult as RevcloudAdvanceResult,
  AdvanceWithPointContext,
  AdvanceWithPointContext as RevcloudPointCtx,
  AdvanceWithPointFn,
  AdvanceWithTextContext,
  AdvanceWithTextContext as RevcloudTextCtx,
  AdvanceWithTextFn,
  DraftCommitResult,
  DraftPoint,
  DraftSession,
  DraftStep,
} from "../draftTypes";
import {
  distanceBetween,
  DRAFT_POINT_PICK_TOLERANCE,
} from "../draftTypes";
import {
  advanceStep,
  branchByOption,
  commitGeometry,
  extractScalarFromDraftText,
  parseScalarAndAdvance,
} from "./advanceHelpers";
import { DIMENSION_POINT_ENTRIES } from "./dimensionRegistry";
import {
  BLOCK_AUTHORING_POINT_ENTRIES,
  BLOCK_AUTHORING_TEXT_ENTRIES,
} from "./blockAuthoringRegistry";
import {
  MODIFY_POINT_ENTRIES,
  MODIFY_TEXT_ENTRIES,
} from "./modifyRegistry";
// VPCLIP-INTERACTIVE — Select-viewport + rectangular clip-boundary pick.
import { VPCLIP_POINT_ENTRIES, VPCLIP_TEXT_ENTRIES } from "./vpclipRegistry";
// REVCLOUD-MODES — persisted defaults + keyword matchers + Freehand
// path commit (see revcloudDraft.ts).
import {
  commitRevcloudFreehand,
  matchRevcloudFirstPromptKeyword,
  matchRevcloudReverseKeyword,
  matchRevcloudStyleKeyword,
  revcloudSessionArcLength,
  revcloudSessionMode,
  revcloudSessionStyle,
  setRevcloudDefaultArcLength,
  setRevcloudDefaultMode,
  setRevcloudDefaultStyle,
} from "./revcloudDraft";

// ─────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────

// Phase 5.3.a — `AdvanceResult` lives in `draftTypes.ts` so helper
// modules (`advanceHelpers.ts`) can reference it without forming an
// import cycle back into the dispatcher. Re-exported here for
// backward compatibility with existing importers.
export type { AdvanceResult } from "../draftTypes";

// Phase 5.3.a/d — context + handler types live in `draftTypes.ts` so
// helper / registry modules can reference them without forming an
// import cycle back into the dispatcher. Re-exported here for
// backward compatibility with existing importers.
export type {
  AdvanceWithPointContext,
  AdvanceWithTextContext,
  AdvanceWithPointFn,
  AdvanceWithTextFn,
} from "../draftTypes";

// ─────────────────────────────────────────────────────────────────────
// Local helpers (verbatim from draftStateMachine.ts)
// ─────────────────────────────────────────────────────────────────────

const MODEL_SPACE_ID = "model";

function modelExtentFromEntities(
  entities: ReadonlyArray<EntityPreview>,
): ModelExtent | undefined {
  const modelEntities = entities.filter(
    (entity) => entity.spaceId === MODEL_SPACE_ID,
  );
  return computeModelExtent(modelEntities) ?? undefined;
}

function withZ(point: DraftPoint) {
  return { x: point.x, y: point.y, z: 0 };
}

function isPositiveFiniteNumber(text: string): boolean {
  const value = Number(text);
  return Number.isFinite(value) && value > 0;
}

/**
 * Extract a scalar numeric value from a draft-text input.
 *
 * AutoCAD-parity background: Dimension Input fields configured with
 * `kind: "length"` build their commit token as a polar coordinate
 * `@<value><angle>` even when the underlying step expects a plain
 * number. This helper recognises both forms.
 */
// (extractScalarFromDraftText now imported from advanceHelpers)

// Phase 5.3.g — `extractScalarFromDraftText` moved to
// `advanceHelpers.ts` so the `parseScalarAndAdvance` primitive can
// use it without forming a cycle back into the dispatcher. Imported
// at the top of this file; the body below replaced the original
// local definition.

/**
 * FIX-4 Cluster B helper — assemble a `geometry/create-block-insert`
 * commit from the INSERT session's accumulated options and end the
 * draft session.
 */
function finalizeInsertSession(
  session: DraftSession,
  activeSpaceId: string,
  overrides: { scaleX?: number; rotation?: number },
): { nextSession: DraftSession | null; commit: DraftCommitResult } {
  const blockName = session.options.blockName ?? "";
  const insertionX = parseFloat(session.options.insertionX ?? "");
  const insertionY = parseFloat(session.options.insertionY ?? "");
  const optScaleX = parseFloat(session.options.scaleX ?? "");
  const optScaleY = parseFloat(session.options.scaleY ?? "");
  const optRotation = parseFloat(session.options.rotation ?? "");
  const uniformScale = session.options.uniformScale === "true";

  const scaleX =
    overrides.scaleX !== undefined && Number.isFinite(overrides.scaleX)
      ? overrides.scaleX
      : Number.isFinite(optScaleX)
        ? optScaleX
        : 1;
  const scaleY = uniformScale
    ? scaleX
    : Number.isFinite(optScaleY)
      ? optScaleY
      : scaleX;
  const rotation =
    overrides.rotation !== undefined && Number.isFinite(overrides.rotation)
      ? overrides.rotation
      : Number.isFinite(optRotation)
        ? optRotation
        : 0;

  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/create-block-insert",
        source: "scene",
        spaceId: activeSpaceId,
        blockName: blockName || "BLOCK_REF",
        insertionPoint: {
          x: Number.isFinite(insertionX) ? insertionX : 0,
          y: Number.isFinite(insertionY) ? insertionY : 0,
        },
        scaleX,
        scaleY,
        rotation,
      },
      summary: t("summary.insert_created"),
      commandText: "INSERT",
    },
  };
}

// ─────────────────────────────────────────────────────────────────────
// Point dispatcher per-step callbacks
// ─────────────────────────────────────────────────────────────────────

/**
 * Per-DraftStep callbacks for `advanceDraftSessionWithPoint`. Order
 * mirrors the legacy switch in `draftStateMachine.ts:146` for
 * review-friendliness.
 *
 * Steps absent from this table fall through to `{ nextSession: session }`
 * in the dispatcher (matches the legacy `default:` branch byte-for-byte).
 */
export const ADVANCE_WITH_POINT: Readonly<
  Partial<Record<DraftStep, AdvanceWithPointFn>>
> = {
  // ── LINE ──────────────────────────────────────────────────────────
  // Phase 5.3.a — migrated to `advanceStep` primitive.
  "line-first-point": (session, point) =>
    advanceStep(session, point, "line-next-point", { closed: false }),
  "line-next-point": (session, point, ctx) => {
    if (session.points.length === 0) {
      return {
        nextSession: {
          ...session,
          points: [point],
          hoverPoint: null,
          closed: false,
        },
      };
    }
    const segmentStart =
      session.points.at(-1) ?? session.points[0] ?? point;
    return {
      nextSession: {
        ...session,
        step: "line-next-point",
        points: [...session.points, point],
        hoverPoint: null,
        closed: false,
      },
      commit: {
        geometryCommand: {
          type: "geometry/create-line",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          start: withZ(segmentStart),
          end: withZ(point),
        },
        summary: `LINE created from ${formatPoint(segmentStart)} to ${formatPoint(point)}.`,
        commandText: "LINE",
      },
    };
  },

  // ── POINT ─────────────────────────────────────────────────────────
  // Phase 5.3.b — migrated to `commitGeometry` primitive.
  "point-location": (session, point, ctx) =>
    commitGeometry(
      {
        type: "geometry/create-point",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        location: withZ(point),
        rotation: 0,
      },
      t("summary.point_created", { point: formatPoint(point) }),
      "POINT",
    ),

  // ── BASE / БАЗА ───────────────────────────────────────────────────
  // Single point pick that sets the drawing insertion base point
  // ($INSBASE) via the Command Engine. Document-level header mutation —
  // no spaceId, no entity created. Undo restores the prior base
  // (computeInverse: header/set-insertion-base ↔ prior point).
  "insbase-point": (_session: DraftSession, point: DraftPoint) =>
    commitGeometry(
      {
        type: "header/set-insertion-base",
        source: "scene",
        point: withZ(point),
      },
      t("summary.base_set", { point: formatPoint(point) }),
      "BASE",
    ),

  // ── CIRCLE ────────────────────────────────────────────────────────
  // Phase 5.3.c — migrated to `branchByOption` primitive.
  "circle-center": (session, point) =>
    branchByOption(
      session,
      point,
      "circle3PMode",
      "true",
      "circle-3p-second-point",
      "circle-radius",
    ),
  "circle-radius": (session, point, ctx) =>
    commitGeometry(
      {
        type: "geometry/create-circle",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        center: withZ(session.points[0]),
        radius: Math.max(1, distanceBetween(session.points[0], point)),
      },
      `CIRCLE created at ${formatPoint(session.points[0])}.`,
      "CIRCLE",
    ),
  "circle-diameter": (session, point, ctx) => {
    const diameterCenter = session.points[0] ?? point;
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-circle",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(diameterCenter),
          radius: Math.max(1, distanceBetween(diameterCenter, point) / 2),
        },
        summary: `CIRCLE created at ${formatPoint(diameterCenter)} from diameter input.`,
        commandText: "CIRCLE",
      },
    };
  },
  "circle-2p-first-point": (session, point) =>
    advanceStep(session, point, "circle-2p-second-point"),
  "circle-2p-second-point": (session, point, ctx) => {
    const p1 = session.points[0] ?? point;
    const midpoint = {
      x: (p1.x + point.x) / 2,
      y: (p1.y + point.y) / 2,
    };
    const radius = Math.max(1, distanceBetween(p1, point) / 2);
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-circle",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(midpoint),
          radius,
        },
        summary: t("summary.circle_created_2p", {
          point: formatPoint(midpoint),
        }),
        commandText: "CIRCLE",
      },
    };
  },
  "circle-3p-second-point": (session, point) =>
    advanceStep(session, point, "circle-3p-third-point", { mode: "append" }),
  "circle-3p-third-point": (session, point, ctx) => {
    const fit = circumscribingCircleFromThree(
      session.points[0],
      session.points[1],
      point,
    );
    if (!fit) {
      return {
        nextSession: null,
        commit: {
          summary: t("error.circle.3p_collinear"),
          commandText: "CIRCLE",
          feedOnly: true,
        },
      };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-circle",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(fit.center),
          radius: fit.radius,
        },
        summary: t("summary.circle_created_3p", {
          point: formatPoint(fit.center),
        }),
        commandText: "CIRCLE",
      },
    };
  },

  // ── ELLIPSE — center mode (Phase D4) ──────────────────────────────
  "ellipse-center": (session, point) =>
    advanceStep(session, point, "ellipse-major-end"),
  "ellipse-major-end": (session, point) =>
    advanceStep(session, point, "ellipse-minor-distance", { mode: "append" }),
  "ellipse-minor-distance": (session, point, ctx) => {
    const ellipseCenter = session.points[0];
    const ellipseMajorEnd = session.points[1];
    if (!ellipseCenter || !ellipseMajorEnd) {
      return { nextSession: null };
    }
    const minorDistance = Math.max(
      1e-6,
      distanceBetween(ellipseCenter, point),
    );
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-ellipse",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(ellipseCenter),
          majorAxisEnd: withZ(ellipseMajorEnd),
          minorDistance,
          startParam: 0,
          endParam: Math.PI * 2,
        },
        summary: t("summary.ellipse_created", {
          point: formatPoint(ellipseCenter),
        }),
        commandText: "ELLIPSE",
      },
    };
  },

  // ── ARC ───────────────────────────────────────────────────────────
  "arc-start": (session, point) =>
    advanceStep(session, point, "arc-second"),
  "arc-second": (session, point) =>
    advanceStep(session, point, "arc-end", { mode: "append" }),
  "arc-end": (session, point, ctx) => {
    const arcGeometry = resolveArcGeometry([...session.points, point]);
    if (!arcGeometry) {
      // AutoCAD parity: the default ARC flow is 3-point (3 picks). Three
      // collinear (or coincident) picks describe a degenerate arc of
      // infinite radius — AutoCAD aborts the command with a feed-only
      // message and creates NO entity. `resolveArcGeometry` returns
      // `null` on the zero-area-triangle determinant test (same
      // collinearity math as `circumscribingCircleFromThree` powering
      // CIRCLE 3P), so we branch on that and end the draft exactly like
      // the `circle-3p-third-point` collinear abort above: `nextSession:
      // null` (session ends, no stale anchors retained) + `feedOnly: true`
      // (skip the entity-create / geometry-dispatch path) + the
      // `error.arc.3p_collinear` toast.
      return {
        nextSession: null,
        commit: {
          summary: t("error.arc.3p_collinear"),
          commandText: "ARC",
          feedOnly: true,
        },
      };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-arc",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(arcGeometry.center),
          radius: arcGeometry.radius,
          startAngle: arcGeometry.startAngle,
          endAngle: arcGeometry.endAngle,
        },
        summary: t("summary.arc_created"),
        commandText: "ARC",
      },
    };
  },
  // Phase C.3 — ARC Start-Center-End. After typing C/Center at
  // arc-second the user clicks the center; the next step
  // (arc-sce-end-angle) is text-only.
  "arc-sce-center": (session, point) =>
    advanceStep(session, point, "arc-sce-end-angle", { mode: "append" }),

  // ── POLYGON ──────────────────────────────────────────────────────
  "polygon-center": (session, point) =>
    advanceStep(session, point, "polygon-radius"),
  "polygon-radius": (session, point, ctx) => {
    const polyCenter = session.points[0];
    if (!polyCenter) {
      return { nextSession: null };
    }
    const polyRadius = distanceBetween(polyCenter, point);
    if (!Number.isFinite(polyRadius) || polyRadius <= 0) {
      return { nextSession: session };
    }
    const polySides = Math.max(
      3,
      Math.min(
        1024,
        Math.floor(Number(session.options.polygonSides ?? "4")) || 4,
      ),
    );
    const polyMode: "inscribed" | "circumscribed" =
      session.options.polygonMode === "circumscribed"
        ? "circumscribed"
        : "inscribed";
    const polyVerts = generatePolygonVertices(
      polyCenter,
      polySides,
      polyRadius,
      polyMode,
    );
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-lwpolyline",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          vertices: polyVerts.map((v) => ({
            x: v.x,
            y: v.y,
            bulge: 0,
            startWidth: 0,
            endWidth: 0,
          })),
          closed: true,
        },
        summary: t("summary.polygon_created", {
          sides: String(polySides),
        }),
        commandText: "POLYGON",
      },
    };
  },

  // ── DONUT ────────────────────────────────────────────────────────
  "donut-center": (session, point, ctx) => {
    const innerD = Number(session.options.donutInnerD ?? "0.5");
    const outerD = Number(session.options.donutOuterD ?? "1.0");
    if (
      !Number.isFinite(innerD) ||
      !Number.isFinite(outerD) ||
      innerD < 0 ||
      outerD <= 0
    ) {
      return { nextSession: null };
    }
    const [normalizedInnerD, normalizedOuterD] =
      innerD > outerD ? [outerD, innerD] : [innerD, outerD];
    return {
      nextSession: {
        ...session,
        step: "donut-center",
        points: [],
        hoverPoint: null,
        options: {
          ...session.options,
          donutInnerD: String(normalizedInnerD),
          donutOuterD: String(normalizedOuterD),
        },
      },
      commit: {
        geometryCommand: {
          type: "geometry/create-donut",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(point),
          innerDiameter: normalizedInnerD,
          outerDiameter: normalizedOuterD,
        },
        summary: t("summary.donut_created", {
          point: formatPoint(point),
        }),
        commandText: "DONUT",
      },
    };
  },

  // ── PLINE ────────────────────────────────────────────────────────
  "polyline-first-point": (session, point) => {
    const startW = parseFloat(session.options.currentStartWidth ?? "0");
    const endW = parseFloat(session.options.currentEndWidth ?? "0");
    const widthArray0 = [
      {
        startWidth: Number.isFinite(startW) ? startW : 0,
        endWidth: Number.isFinite(endW) ? endW : 0,
      },
    ];
    return {
      nextSession: {
        ...session,
        step: "polyline-next-point",
        points: [point],
        hoverPoint: null,
        options: {
          ...session.options,
          polylineWidths: JSON.stringify(widthArray0),
        },
      },
    };
  },
  "polyline-next-point": (session, point) => {
    const startWNext = parseFloat(session.options.currentStartWidth ?? "0");
    const endWNext = parseFloat(session.options.currentEndWidth ?? "0");
    let widthArray: { startWidth: number; endWidth: number }[] = [];
    if (session.options.polylineWidths) {
      try {
        const parsed = JSON.parse(session.options.polylineWidths);
        if (Array.isArray(parsed)) {
          widthArray = parsed.filter(
            (w): w is { startWidth: number; endWidth: number } =>
              typeof w === "object" &&
              w !== null &&
              typeof (w as { startWidth: unknown }).startWidth === "number" &&
              typeof (w as { endWidth: unknown }).endWidth === "number",
          );
        }
      } catch {
        widthArray = [];
      }
    }
    widthArray.push({
      startWidth: Number.isFinite(startWNext) ? startWNext : 0,
      endWidth: Number.isFinite(endWNext) ? endWNext : 0,
    });
    return {
      nextSession: {
        ...session,
        step: "polyline-next-point",
        points: [...session.points, point],
        hoverPoint: null,
        closed: false,
        options: {
          ...session.options,
          polylineWidths: JSON.stringify(widthArray),
        },
      },
    };
  },

  // ── RECTANGLE ────────────────────────────────────────────────────
  "rectangle-first-corner": (session, point) => {
    if (session.options.rectDPending === "true") {
      return {
        nextSession: {
          ...session,
          step: "rectangle-d-place",
          points: [point],
          hoverPoint: null,
          options: { ...session.options, rectDPending: "false" },
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "rectangle-second-corner",
        points: [point],
        hoverPoint: null,
      },
    };
  },
  "rectangle-second-corner": (session, point, ctx) => {
    const rectCorners = rectangleCorners(session.points[0], point);
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-lwpolyline",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          vertices: rectCorners.map((c) => ({
            x: c.x,
            y: c.y,
            bulge: 0,
            startWidth: 0,
            endWidth: 0,
          })),
          closed: true,
        },
        summary: t("summary.rectang_created"),
        commandText: "RECTANG",
      },
    };
  },
  "rectangle-d-place": (session, point, ctx) => {
    const first = session.points[0];
    const rdLength = parseFloat(session.options.rectDLength ?? "");
    const rdWidth = parseFloat(session.options.rectDWidth ?? "");
    if (
      !Number.isFinite(rdLength) ||
      !Number.isFinite(rdWidth) ||
      rdLength <= 0 ||
      rdWidth <= 0
    ) {
      return { nextSession: session };
    }
    const signX = point.x >= first.x ? 1 : -1;
    const signY = point.y >= first.y ? 1 : -1;
    const second = {
      x: first.x + signX * rdLength,
      y: first.y + signY * rdWidth,
    };
    const rectCornersDim = rectangleCorners(first, second);
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-lwpolyline",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          vertices: rectCornersDim.map((c) => ({
            x: c.x,
            y: c.y,
            bulge: 0,
            startWidth: 0,
            endWidth: 0,
          })),
          closed: true,
        },
        summary: t("summary.rectang_created"),
        commandText: "RECTANG",
      },
    };
  },

  // ── TEXT ─────────────────────────────────────────────────────────
  "text-insertion-point": (session, point) => ({
    nextSession: {
      ...session,
      step: "text-height",
      points: [point],
      hoverPoint: null,
    },
  }),
  "text-height": (session, point) => {
    const insertPt = session.points[0];
    const dist = Math.hypot(point.x - insertPt.x, point.y - insertPt.y);
    const heightVal = Math.max(1, Math.round(dist * 10) / 10);
    return {
      nextSession: {
        ...session,
        step: "text-rotation" as DraftStep,
        options: { ...session.options, height: String(heightVal) },
      },
    };
  },
  "text-rotation": (session, point) => {
    const insPt = session.points[0];
    const angleDeg =
      Math.round(
        ((Math.atan2(point.y - insPt.y, point.x - insPt.x) * 180) / Math.PI) *
          10,
      ) / 10;
    return {
      nextSession: {
        ...session,
        step: "text-content" as DraftStep,
        options: { ...session.options, rotation: String(-angleDeg) },
      },
    };
  },

  // ── MTEXT ────────────────────────────────────────────────────────
  "mtext-first-corner": (session, point) => ({
    nextSession: {
      ...session,
      step: "mtext-second-corner",
      points: [point],
      hoverPoint: null,
    },
  }),
  "mtext-second-corner": (session, point, ctx) => {
    // FIX-4 Cluster A: commit MTEXT immediately on second-corner click
    // with default content "MText".
    const mtextPt0 = session.points[0];
    const mtextWidth = Math.max(48, Math.abs(point.x - mtextPt0.x));
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-mtext",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          insertionPoint: { x: mtextPt0.x, y: mtextPt0.y },
          nominalHeight: 24,
          referenceWidth: mtextWidth,
          contents: "MText",
          styleName: "Standard",
        },
        summary: t("summary.mtext_created"),
        commandText: "MTEXT",
      },
    };
  },

  // ── BLOCK (legacy + Phase 1) ─────────────────────────────────────
  "block-name": (session, point) => ({
    nextSession: {
      ...session,
      step: "block-scale",
      points: [point],
      hoverPoint: null,
      options: {
        ...session.options,
        blockName: session.options.blockName ?? "BLOCK_REF",
      },
    },
  }),
  "block-insertion-point": (session, point) => ({
    nextSession: {
      ...session,
      step: "block-scale",
      points: [point],
      hoverPoint: null,
    },
  }),
  "block-scale": (session) => ({ nextSession: session }),
  "block-rotation": (session) => ({ nextSession: session }),
  "block-select-objects": (session) => ({ nextSession: session }),
  "block-base-point": (session, point) => ({
    nextSession: {
      ...session,
      step: "block-dialog",
      points: [point],
      hoverPoint: null,
      options: {
        ...session.options,
        blockBaseX: String(point.x),
        blockBaseY: String(point.y),
      },
    },
  }),
  "block-dialog": (session) => ({ nextSession: session }),

  // ── INSERT ───────────────────────────────────────────────────────
  "insert-dialog": (session) => ({ nextSession: session }),
  "insert-specify-point": (session, point) => ({
    nextSession: {
      ...session,
      step:
        session.options.specifyScaleOnScreen === "true"
          ? "insert-specify-scale"
          : session.options.specifyRotationOnScreen === "true"
            ? "insert-specify-rotation"
            : session.options.pendingAttribTags
              ? "insert-collect-attribs"
              : "insert-dialog",
      points: [point],
      hoverPoint: null,
      options: {
        ...session.options,
        insertionX: String(point.x),
        insertionY: String(point.y),
      },
    },
  }),
  "insert-specify-scale": (session, point, ctx) => {
    const base = session.points[0] ?? { x: 0, y: 0 };
    const dist = Math.hypot(point.x - base.x, point.y - base.y);
    const scale = dist || 1;
    const nextStep =
      session.options.specifyRotationOnScreen === "true"
        ? "insert-specify-rotation"
        : session.options.pendingAttribTags
          ? "insert-collect-attribs"
          : null;
    if (nextStep === null) {
      return finalizeInsertSession(
        {
          ...session,
          points: [...session.points, point],
          options: { ...session.options, scaleX: String(scale) },
        },
        ctx.activeSpaceId,
        { scaleX: scale },
      );
    }
    return {
      nextSession: {
        ...session,
        step: nextStep,
        points: [...session.points, point],
        hoverPoint: null,
        options: {
          ...session.options,
          scaleX: String(scale),
        },
      },
    };
  },
  "insert-specify-rotation": (session, point, ctx) => {
    const base = session.points[0] ?? { x: 0, y: 0 };
    const angle =
      (Math.atan2(point.y - base.y, point.x - base.x) * 180) / Math.PI;
    if (!session.options.pendingAttribTags) {
      return finalizeInsertSession(
        {
          ...session,
          points: [...session.points, point],
          options: { ...session.options, rotation: String(angle) },
        },
        ctx.activeSpaceId,
        { rotation: angle },
      );
    }
    return {
      nextSession: {
        ...session,
        step: "insert-collect-attribs",
        points: [...session.points, point],
        hoverPoint: null,
        options: {
          ...session.options,
          rotation: String(angle),
        },
      },
    };
  },
  "insert-collect-attribs": (session) => ({ nextSession: session }),

  // ── HATCH ────────────────────────────────────────────────────────
  "hatch-pattern": (session, point, ctx) => {
    const seededOptions = {
      ...session.options,
      hatchPattern: session.options.hatchPattern ?? "ANSI31",
      hatchScale: session.options.hatchScale ?? "1",
      hatchSeedPoints: "",
      hatchBoundaryPaths: "",
    };
    const detectedFromPattern = findBoundaryFromSeed(
      point,
      ctx.geometryEntities,
    );
    if (!detectedFromPattern) {
      return {
        nextSession: {
          ...session,
          step: "hatch-pick-internal-point",
          hoverPoint: null,
          options: {
            ...seededOptions,
            hatchLastError: t("error.hatch.no_boundary"),
          },
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "hatch-pick-additional",
        hoverPoint: null,
        options: {
          ...seededOptions,
          hatchSeedPoints: JSON.stringify([{ x: point.x, y: point.y }]),
          hatchBoundaryPaths: JSON.stringify(
            detectedFromPattern.loops.map((loop) => ({
              pathTypeFlag: 1,
              edges: loop,
            })),
          ),
          hatchLastError: "",
        },
      },
    };
  },
  "hatch-pick-internal-point": hatchPickShared,
  "hatch-pick-additional": hatchPickShared,
  "hatch-first-corner": (session, point) => ({
    nextSession: {
      ...session,
      step: "hatch-second-corner",
      points: [point],
      hoverPoint: null,
    },
  }),
  "hatch-second-corner": (session, point) => ({
    nextSession: {
      ...session,
      step: "hatch-ready",
      points: [session.points[0], point],
      hoverPoint: null,
    },
  }),

  // ── MLEADER ──────────────────────────────────────────────────────
  "mleader-arrow-point": (session, point) => ({
    nextSession: {
      ...session,
      step: "mleader-landing-point",
      points: [point],
      hoverPoint: null,
    },
  }),
  "mleader-landing-point": (session, point) => ({
    nextSession: {
      ...session,
      step: "mleader-content",
      points: [...session.points, point],
      hoverPoint: null,
    },
  }),
  "mleaderedit-add-leader-arrow": (session, point, ctx) => {
    const targetEntityId = session.options.targetEntityId;
    if (!targetEntityId) {
      return { nextSession: null };
    }
    const targetEntity = ctx.existingEntities.find(
      (e) => e.id === targetEntityId,
    );
    if (!targetEntity || targetEntity.type !== "Multileader") {
      return {
        nextSession: null,
        commit: {
          summary: t("prompt.mleaderedit.not_mleader"),
          commandText: "MLEADEREDIT",
        },
      };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/add-leader-to-multileader",
          source: "scene",
          entityId: targetEntityId,
          arrowPoint: { x: point.x, y: point.y },
        },
        summary: t("summary.mleader_leader_added"),
        commandText: "MLEADEREDIT",
      },
    };
  },
  "mleaderedit-remove-pick": (session, _point, ctx) => {
    const targetEntityId = session.options.targetEntityId;
    if (!targetEntityId) {
      return { nextSession: null };
    }
    const targetEntity = ctx.existingEntities.find(
      (e) => e.id === targetEntityId,
    );
    if (!targetEntity || targetEntity.type !== "Multileader") {
      return {
        nextSession: null,
        commit: {
          summary: t("prompt.mleaderedit.not_mleader"),
          commandText: "MLEADEREDIT",
        },
      };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/remove-leader-from-multileader",
          source: "scene",
          entityId: targetEntityId,
          leaderIndex: 0,
        },
        summary: t("summary.mleader_leader_removed"),
        commandText: "MLEADEREDIT",
      },
    };
  },

  // ── REVCLOUD ────────────────────────────────────────────────────
  // REVCLOUD-MODES — the first pick starts drawing in the CURRENT
  // creation method (AutoCAD: the method chosen via the R/P/F keywords
  // persists across invocations — REVCLOUDCREATEMODE; persisted in
  // revcloudDraft.ts until the REVCLOUD-SYSVARS slice re-routes it).
  "revcloud-first-corner": (
    session: DraftSession,
    point: DraftPoint,
  ): RevcloudAdvanceResult => {
    const rcMode = revcloudSessionMode(session);
    if (rcMode === "Polygonal") {
      return {
        nextSession: {
          ...session,
          step: "revcloud-polygonal-next",
          points: [point],
          hoverPoint: null,
        },
      };
    }
    if (rcMode === "Freehand") {
      return {
        nextSession: {
          ...session,
          step: "revcloud-freehand-path",
          points: [point],
          hoverPoint: null,
        },
      };
    }
    if (rcMode === "Object") {
      // Object mode has no "first point" — route the pick to the
      // object-select handler directly.
      return {
        nextSession: {
          ...session,
          step: "revcloud-object-pick",
          points: [],
          hoverPoint: null,
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "revcloud-second-corner",
        points: [point],
        hoverPoint: null,
      },
    };
  },
  "revcloud-second-corner": (
    session: DraftSession,
    point: DraftPoint,
    ctx: RevcloudPointCtx,
  ): RevcloudAdvanceResult =>
    commitGeometry(
      {
        type: "geometry/create-revcloud",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        creationMode: "Rectangular",
        corner1: { x: session.points[0].x, y: session.points[0].y },
        corner2: { x: point.x, y: point.y },
        // Consume the Arc-length / Style keyword results (previously a
        // hardcoded arcChordLength: 1).
        arcChordLength: revcloudSessionArcLength(session),
        revisionCloudStyle: revcloudSessionStyle(session),
        reverseDirection: false,
      },
      t("summary.revcloud_created"),
      "REVCLOUD",
    ),
  // Polygonal — collect vertices; empty Enter (text handler) closes the
  // polygon once >= 3 vertices were picked (AutoCAD "Specify next point").
  "revcloud-polygonal-next": (
    session: DraftSession,
    point: DraftPoint,
  ): RevcloudAdvanceResult => ({
    nextSession: {
      ...session,
      points: [...session.points, point],
      hoverPoint: null,
    },
  }),
  // Freehand — hover sampling owns the path (useDraftSession routes
  // hover through advanceRevcloudFreehandHover); a CLICK near the start
  // closes early, any other click appends an anchor sample.
  "revcloud-freehand-path": (
    session: DraftSession,
    point: DraftPoint,
    ctx: RevcloudPointCtx,
  ): RevcloudAdvanceResult => {
    const rcStart = session.points[0];
    const rcArcLen = revcloudSessionArcLength(session);
    if (
      rcStart &&
      session.points.length >= 3 &&
      distanceBetween(point, rcStart) <= rcArcLen
    ) {
      return commitRevcloudFreehand(session, ctx.activeSpaceId);
    }
    return {
      nextSession: {
        ...session,
        points: [...session.points, point],
        hoverPoint: null,
      },
    };
  },
  // Object — pick a CLOSED source entity (circle / ellipse / closed
  // polyline / closed spline). The pick is resolved against the typed
  // geometry entities (outline tracing needs real primitives, like the
  // HATCH internal-pick path); unsupported / open picks are rejected
  // the way AutoCAD rejects non-closed objects.
  "revcloud-object-pick": (
    session: DraftSession,
    point: DraftPoint,
    ctx: RevcloudPointCtx,
  ): RevcloudAdvanceResult => {
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const candidate of ctx.geometryEntities) {
      // Hit-testing is GEOMETRIC, not arc-length-dependent: trace with the
      // FINE clamp-floor chord (`MIN_REVCLOUD_ARC_CHORD` = 1) so a large
      // user arc length (e.g. 200 on a radius-100 circle → square) cannot
      // coarsen the pick polygon away from the true source boundary. The
      // CONVERSION itself (committed at the Reverse prompt) still uses the
      // user's arc length via `revcloudSessionArcLength`.
      const outline = revisionCloudOutlineFromSource(
        candidate,
        MIN_REVCLOUD_ARC_CHORD,
      );
      if (!outline) continue;
      // Boundary distance, not vertex distance — AutoCAD accepts a pick
      // anywhere ON the source curve (e.g. the middle of a long polyline
      // edge whose corner vertices are far away). The traced outline is
      // a closed chain (first vertex NOT repeated), so the segment loop
      // wraps around via `% length`.
      for (let i = 0; i < outline.length; i++) {
        const segStart = outline[i]!;
        const segEnd = outline[(i + 1) % outline.length]!;
        const d = distancePointToSegment(point, segStart, segEnd);
        if (d < bestDist) {
          bestDist = d;
          bestId = candidate.id;
        }
      }
    }
    if (!bestId || bestDist > DRAFT_POINT_PICK_TOLERANCE) {
      // AutoCAD: a pick that misses every eligible object reports
      // "No object found." and RE-PROMPTS "Select object:" — the
      // session stays on this step. Without the tolerance gate a click
      // arbitrarily far from any boundary silently converted the
      // nearest candidate. `invalid_object` is kept for the
      // no-closed-candidates case (only open / unsupported sources in
      // the drawing — AutoCAD's "not a closed object" rejection).
      return {
        nextSession: session,
        commit: {
          feedOnly: true,
          summary: t(
            bestId
              ? "prompt.revcloud.no_object_found"
              : "prompt.revcloud.invalid_object",
          ),
          commandText: "REVCLOUD",
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "revcloud-object-reverse",
        hoverPoint: null,
        options: { ...session.options, revcloudSourceEntityId: bestId },
      },
    };
  },

  // ── WIPEOUT ─────────────────────────────────────────────────────
  "wipeout-first-corner": (session, point) => ({
    nextSession: {
      ...session,
      step: "wipeout-second-corner",
      points: [point],
      hoverPoint: null,
    },
  }),
  "wipeout-second-corner": (session, point, ctx) => {
    const corner1 = session.points[0];
    const corner2 = point;
    const wipeoutVertices = [
      { x: corner1.x, y: corner1.y },
      { x: corner2.x, y: corner1.y },
      { x: corner2.x, y: corner2.y },
      { x: corner1.x, y: corner2.y },
    ];
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-wipeout",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          vertices: wipeoutVertices,
          frame: true,
        },
        summary: t("summary.wipeout_created"),
        commandText: "WIPEOUT",
      },
    };
  },

  // ── VIEWPORT ────────────────────────────────────────────────────
  "viewport-first-corner": (session, point) => ({
    nextSession: {
      ...session,
      step: "viewport-second-corner",
      points: [point],
      hoverPoint: null,
    },
  }),
  "viewport-second-corner": (session, point, ctx) => {
    const vpModelExtent = modelExtentFromEntities(ctx.existingEntities);
    // Coordinate frame — the SAME boundary `vpclipRegistry.ts`'s
    // `vpclip-second-corner` documents, for the same reason.
    //
    // `createViewportEntity` stores `center = midpoint(corner1, corner2)`
    // straight onto the persisted `ViewportEntity.center`, and that field is
    // SHEET-LOCAL: `ViewportFrameLayer.tsx` / `viewportTransform2d.ts` /
    // `viewportRenderPass.ts` all place the viewport at
    // `center + paperOrigin`. The picked `point`/`points[0]` are DOCUMENT
    // coordinates (DRAFT-DOCUMENT-FRAME: `clientToWorld` unprojects against
    // the offset-world camera, but `pickLoop/interactivePickLoop.ts`
    // converts back at the `onCanvasPoint` emit via
    // `ActiveInputSpace.toDocument`), and `ctx.paperOrigin` is that layout's
    // sheet origin in the SAME document frame
    // (`useDraftSession.resolveDraftPaperOriginDocument` — deliberately NOT
    // the offset-world `Scene3DStage.paperOriginOffset` the renderer uses).
    //
    // So the subtraction is plain sheet-local arithmetic within one frame.
    // It was also correct under the previous convention, where both operands
    // were OFFSET-world, because the difference of two paper quantities is
    // invariant under the pure translation separating the frames:
    // `(pRaw − O) − (originRaw − O) === pRaw − originRaw`. Either way the
    // committed corners are exactly the sheet-local document values AutoCAD
    // would show — no `unapplyOffset` on top, which would push `+O` into a
    // persisted field. What is NOT optional is that both operands share one
    // frame.
    //
    // Without this subtraction MVIEW committed the raw pick, so on any layout
    // with a non-zero `LayoutPageSetup.origin` the new viewport rendered a
    // whole origin away from where the user dragged it (and a whole SESSION
    // origin away on a survey-scale drawing).
    const origin = ctx.paperOrigin ?? { x: 0, y: 0 };
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-viewport",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          corner1: {
            x: session.points[0].x - origin.x,
            y: session.points[0].y - origin.y,
          },
          corner2: { x: point.x - origin.x, y: point.y - origin.y },
          ...(vpModelExtent ? { modelExtent: vpModelExtent } : {}),
        },
        summary: t("summary.viewport_created"),
        commandText: "MVIEW",
      },
    };
  },

  // ── SPLINE ──────────────────────────────────────────────────────
  "spline-fit-first": (session, point) => ({
    nextSession: {
      ...session,
      step: "spline-fit-next",
      points: [...session.points, point],
      hoverPoint: null,
    },
  }),
  "spline-fit-next": (session, point) => ({
    nextSession: {
      ...session,
      step: "spline-fit-next",
      points: [...session.points, point],
      hoverPoint: null,
    },
  }),

  // ── DIMENSIONS (Phase 5.3.d sub-machine collapse) ──
  // Each of the 21 dim-* steps delegates verbatim to
  // `advanceDimensionWithPoint` so the unified dispatcher in
  // `draftStateMachine.ts` no longer needs the `isDimensionStep`
  // predicate + delegation block.
  ...Object.fromEntries(DIMENSION_POINT_ENTRIES),

  // ── BLOCK AUTHORING (Phase 5.3.e sub-machine collapse) ──
  // Phase K.2 / K.3 placement steps delegate verbatim to
  // `advanceBlockAuthoringWithPoint`; the `isBlockAuthoringStep`
  // predicate + delegation in `draftStateMachine.ts` is gone.
  ...Object.fromEntries(BLOCK_AUTHORING_POINT_ENTRIES),

  // ── MODIFY (Phase 5.3.f sub-machine collapse) ──
  // MOVE / COPY / MIRROR / ROTATE / SCALE / OFFSET / STRETCH /
  // TRIM / EXTEND / FILLET / CHAMFER / BREAK / JOIN / EXPLODE /
  // LENGTHEN / ARRAYRECT / ARRAYPOLAR / ARRAYPATH / ARRAYEDIT /
  // GCCONSTRAINT — 60 steps total. Delegate verbatim to
  // `advanceModifySessionWithPoint`. The `isModifyStep` predicate
  // + delegation block in `draftStateMachine.ts` is gone.
  ...Object.fromEntries(MODIFY_POINT_ENTRIES),

  // ── VPCLIP-INTERACTIVE (W2.6) ──
  // Select-viewport hit-test + rectangular clip-boundary two-corner pick.
  // Handler bodies live in `vpclipRegistry.ts` (kept out of this file to
  // respect the CLAUDE.md 2000-line file-size ceiling this file is already
  // well past).
  ...Object.fromEntries(VPCLIP_POINT_ENTRIES),
};

/**
 * Shared body for `hatch-pick-internal-point` + `hatch-pick-additional`
 * (both steps had identical case bodies in the legacy switch).
 */
function hatchPickShared(
  session: DraftSession,
  point: DraftPoint,
  ctx: AdvanceWithPointContext,
): AdvanceResult {
  const detected = findBoundaryFromSeed(point, ctx.geometryEntities);
  if (!detected) {
    return {
      nextSession: {
        ...session,
        options: {
          ...session.options,
          hatchLastError: t("error.hatch.no_boundary"),
        },
      },
    };
  }
  const prevSeeds: Array<{ x: number; y: number }> = (() => {
    try {
      return JSON.parse(session.options.hatchSeedPoints ?? "[]");
    } catch {
      return [];
    }
  })();
  const prevPaths: Array<{ pathTypeFlag: number; edges: BoundaryLoop }> = (() => {
    try {
      return JSON.parse(session.options.hatchBoundaryPaths ?? "[]");
    } catch {
      return [];
    }
  })();
  const newSeeds = [...prevSeeds, { x: point.x, y: point.y }];
  const newPaths = [
    ...prevPaths,
    ...detected.loops.map((loop) => ({
      pathTypeFlag: 1,
      edges: loop,
    })),
  ];
  return {
    nextSession: {
      ...session,
      step: "hatch-pick-additional",
      hoverPoint: null,
      options: {
        ...session.options,
        hatchSeedPoints: JSON.stringify(newSeeds),
        hatchBoundaryPaths: JSON.stringify(newPaths),
        hatchLastError: "",
      },
    },
  };
}

// ─────────────────────────────────────────────────────────────────────
// Text dispatcher per-step callbacks
// ─────────────────────────────────────────────────────────────────────

/**
 * Per-DraftStep callbacks for `advanceDraftSessionWithText`.
 *
 * **Important:** the legacy switch in `draftStateMachine.ts:1313` is NOT
 * a clean per-step switch. It mixes:
 *   - `command`-level checks (LINE close, PLINE close/undo)
 *   - step+keyword combinations (CIRCLE 2P/3P, ARC SCE, RECTANGLE D)
 *   - the trailing point-parser fallback (every step that accepts coord)
 *
 * Per-step callbacks below cover only the **pure step-bound** branches —
 * the dispatcher routes through this table FIRST, then falls back to the
 * cross-cutting / order-sensitive logic that doesn't fit a per-step
 * callback shape.
 *
 * Callbacks return:
 *   - `AdvanceResult` — handled (forwards to caller).
 *   - `null`         — not handled, fall through to next layer
 *                      (cross-cutting branch / point-parser / final
 *                      `{ nextSession: session }`).
 */
export const ADVANCE_WITH_TEXT: Readonly<
  Partial<Record<DraftStep, AdvanceWithTextFn>>
> = {
  "polyline-width-start": (session, ctx) => {
    if (session.command !== "PLINE") return null;
    const startWidthValue = extractScalarFromDraftText(ctx.text);
    if (
      startWidthValue === null ||
      !Number.isFinite(startWidthValue) ||
      startWidthValue < 0
    ) {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "polyline-width-end",
        hoverPoint: null,
        options: {
          ...session.options,
          currentStartWidth: String(startWidthValue),
        },
      },
    };
  },
  "polyline-width-end": (session, ctx) => {
    if (session.command !== "PLINE") return null;
    const endWidthValue = extractScalarFromDraftText(ctx.text);
    if (
      endWidthValue === null ||
      !Number.isFinite(endWidthValue) ||
      endWidthValue < 0
    ) {
      return { nextSession: session };
    }
    const returnStepRaw = session.options.polylineWidthReturnStep ?? "";
    const returnStep: DraftStep =
      returnStepRaw === "polyline-first-point" ||
      returnStepRaw === "polyline-next-point"
        ? returnStepRaw
        : session.points.length === 0
          ? "polyline-first-point"
          : "polyline-next-point";
    return {
      nextSession: {
        ...session,
        step: returnStep,
        hoverPoint: null,
        options: {
          ...session.options,
          currentEndWidth: String(endWidthValue),
        },
      },
    };
  },

  "arc-sce-end-angle": (session, ctx) => {
    const sceAngle = extractScalarFromDraftText(ctx.text);
    if (sceAngle !== null && Number.isFinite(sceAngle) && sceAngle !== 0) {
      const sceStart = session.points[0];
      const sceCenter = session.points[1];
      if (sceStart && sceCenter) {
        const projected = arcSceEndPoint(sceStart, sceCenter, sceAngle);
        const midAngle = projected.startAngle + (sceAngle * Math.PI) / 360;
        const sceMid = {
          x: sceCenter.x + projected.radius * Math.cos(midAngle),
          y: sceCenter.y + projected.radius * Math.sin(midAngle),
        };
        const sceArcGeom = resolveArcGeometry([
          sceStart,
          sceMid,
          projected.end,
        ]);
        if (!sceArcGeom) {
          return { nextSession: session };
        }
        return {
          nextSession: null,
          commit: {
            geometryCommand: {
              type: "geometry/create-arc",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              center: withZ(sceArcGeom.center),
              radius: sceArcGeom.radius,
              startAngle: sceArcGeom.startAngle,
              endAngle: sceArcGeom.endAngle,
            },
            summary: t("summary.arc_created"),
            commandText: "ARC",
          },
        };
      }
    }
    return { nextSession: session };
  },

  "rectangle-d-length": (session, ctx) => {
    const lenValue = extractScalarFromDraftText(ctx.text);
    if (lenValue === null || !Number.isFinite(lenValue) || lenValue <= 0) {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "rectangle-d-width",
        hoverPoint: null,
        options: {
          ...session.options,
          rectDLength: String(lenValue),
        },
      },
    };
  },
  "rectangle-d-width": (session, ctx) => {
    const widValue = extractScalarFromDraftText(ctx.text);
    if (widValue === null || !Number.isFinite(widValue) || widValue <= 0) {
      return { nextSession: session };
    }
    const needsFirstCorner = session.points.length === 0;
    return {
      nextSession: {
        ...session,
        step: needsFirstCorner ? "rectangle-first-corner" : "rectangle-d-place",
        hoverPoint: null,
        options: {
          ...session.options,
          rectDWidth: String(widValue),
          rectDPending: needsFirstCorner ? "true" : "false",
        },
      },
    };
  },

  "circle-2p-second-point": (session, ctx) => {
    if (!isPositiveFiniteNumber(ctx.text)) return null;
    const p1 = session.points[0];
    if (!p1) return null;
    const diameterValue = Number(ctx.text);
    const radius = diameterValue / 2;
    const p2 = { x: p1.x + diameterValue, y: p1.y };
    const midpoint = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-circle",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          center: withZ(midpoint),
          radius,
        },
        summary: t("summary.circle_created_2p", {
          point: formatPoint(midpoint),
        }),
        commandText: "CIRCLE",
      },
    };
  },

  "circle-radius": (session, ctx) => {
    const radiusValue = extractScalarFromDraftText(ctx.text);
    if (
      radiusValue !== null &&
      Number.isFinite(radiusValue) &&
      radiusValue > 0
    ) {
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-circle",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            center: withZ(session.points[0]),
            radius: radiusValue,
          },
          summary: t("summary.circle_created", {
            point: formatPoint(session.points[0]),
          }),
          commandText: "CIRCLE",
        },
      };
    }
    return null;
  },

  "ellipse-minor-distance": (session, ctx) => {
    const minorVal = extractScalarFromDraftText(ctx.text);
    if (minorVal !== null && Number.isFinite(minorVal) && minorVal > 0) {
      const ellipseCenterTxt = session.points[0];
      const ellipseMajorEndTxt = session.points[1];
      if (!ellipseCenterTxt || !ellipseMajorEndTxt) {
        return { nextSession: session };
      }
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-ellipse",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            center: withZ(ellipseCenterTxt),
            majorAxisEnd: withZ(ellipseMajorEndTxt),
            minorDistance: minorVal,
            startParam: 0,
            endParam: Math.PI * 2,
          },
          summary: t("summary.ellipse_created", {
            point: formatPoint(ellipseCenterTxt),
          }),
          commandText: "ELLIPSE",
        },
      };
    }
    return null;
  },

  // Phase 5.3.g — migrated to `parseScalarAndAdvance` primitive.
  "donut-inner-d": (session, ctx) =>
    parseScalarAndAdvance(session, ctx, {
      optionKey: "donutInnerD",
      defaultValue: "0.5",
      nextStep: "donut-outer-d",
      validate: (raw) => (raw >= 0 ? raw : null),
    }),
  "donut-outer-d": (session, ctx) =>
    parseScalarAndAdvance(session, ctx, {
      optionKey: "donutOuterD",
      defaultValue: "1.0",
      nextStep: "donut-center",
      validate: (raw) => (raw > 0 ? raw : null),
    }),
  "donut-center": (_session, ctx) => {
    // Empty Enter / EXIT / Выход ends the multi-donut loop.
    // Typed coords are forwarded to the point-handler (registry returns
    // null → dispatcher's point-parser tries to interpret).
    if (
      ctx.text === "" ||
      ctx.normalizedText === "EXIT" ||
      ctx.normalizedText === "E" ||
      ctx.normalizedText === "ВЫХОД" ||
      ctx.normalizedText === "В"
    ) {
      return { nextSession: null };
    }
    return null;
  },

  // Phase 5.3.g — migrated to `parseScalarAndAdvance` primitive.
  "polygon-sides-input": (session, ctx) =>
    parseScalarAndAdvance(session, ctx, {
      optionKey: "polygonSides",
      defaultValue: "4",
      nextStep: "polygon-mode-prompt",
      validate: (raw) => {
        const sides = Math.floor(raw);
        return sides >= 3 && sides <= 1024 ? sides : null;
      },
    }),
  "polygon-mode-prompt": (session, ctx) => {
    let mode: "inscribed" | "circumscribed" = "inscribed";
    const upper = ctx.normalizedText;
    if (ctx.text !== "") {
      if (
        upper === "I" ||
        upper === "INSCRIBED" ||
        upper === "В" ||
        upper === "ВПИСАН" ||
        upper === "ВПИСАННЫЙ"
      ) {
        mode = "inscribed";
      } else if (
        upper === "C" ||
        upper === "CIRCUMSCRIBED" ||
        upper === "О" ||
        upper === "ОПИСАН" ||
        upper === "ОПИСАННЫЙ"
      ) {
        mode = "circumscribed";
      } else {
        return { nextSession: session };
      }
    }
    return {
      nextSession: {
        ...session,
        step: "polygon-center",
        hoverPoint: null,
        options: { ...session.options, polygonMode: mode },
      },
    };
  },
  "polygon-radius": (session, ctx) => {
    const polyCenterTxt = session.points[0];
    if (!polyCenterTxt) {
      return { nextSession: null };
    }
    const radValue = extractScalarFromDraftText(ctx.text);
    if (radValue !== null && Number.isFinite(radValue) && radValue > 0) {
      const sidesTxt = Math.max(
        3,
        Math.min(
          1024,
          Math.floor(Number(session.options.polygonSides ?? "4")) || 4,
        ),
      );
      const modeTxt: "inscribed" | "circumscribed" =
        session.options.polygonMode === "circumscribed"
          ? "circumscribed"
          : "inscribed";
      const polyVertsTxt = generatePolygonVertices(
        polyCenterTxt,
        sidesTxt,
        radValue,
        modeTxt,
      );
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-lwpolyline",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            vertices: polyVertsTxt.map((v) => ({
              x: v.x,
              y: v.y,
              bulge: 0,
              startWidth: 0,
              endWidth: 0,
            })),
            closed: true,
          },
          summary: t("summary.polygon_created", {
            sides: String(sidesTxt),
          }),
          commandText: "POLYGON",
        },
      };
    }
    return null;
  },

  "block-name": (session, ctx) => {
    if (ctx.normalizedText === "?") {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            blockName: "BLOCK_REF",
          },
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "block-insertion-point",
        options: {
          ...session.options,
          blockName: ctx.text || "BLOCK_REF",
        },
      },
    };
  },
  "block-scale": (session, ctx) => {
    const scale = ctx.text === "" ? 1 : extractScalarFromDraftText(ctx.text);
    if (scale !== null && Number.isFinite(scale) && scale > 0) {
      return {
        nextSession: {
          ...session,
          step: "block-rotation",
          options: {
            ...session.options,
            blockScale: formatNumber(scale),
          },
        },
      };
    }
    return null;
  },
  "block-rotation": (session, ctx) => {
    const rotation =
      ctx.text === "" ? 0 : (extractScalarFromDraftText(ctx.text) ?? NaN);
    if (Number.isFinite(rotation)) {
      const blockScale = Number(session.options.blockScale ?? "1") || 1;
      const insertPt = session.points[0] ?? { x: 0, y: 0 };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-block-insert",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            blockName: session.options.blockName ?? "BLOCK_REF",
            insertionPoint: { x: insertPt.x, y: insertPt.y },
            scaleX: blockScale,
            scaleY: blockScale,
            rotation,
          },
          summary: t("summary.insert_created"),
          commandText: "INSERT",
        },
      };
    }
    return null;
  },
  "circle-diameter": (session, ctx) => {
    const diameter = extractScalarFromDraftText(ctx.text);
    const diameterCenter = session.points[0] ?? { x: 0, y: 0 };
    if (diameter !== null && Number.isFinite(diameter) && diameter > 0) {
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-circle",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            center: withZ(diameterCenter),
            radius: diameter / 2,
          },
          summary: t("summary.circle_created_diameter", {
            point: formatPoint(diameterCenter),
            diameter: formatNumber(diameter),
          }),
          commandText: "CIRCLE",
        },
      };
    }
    return null;
  },
  "insert-specify-scale": (session, ctx) => {
    const scale = ctx.text === "" ? 1 : extractScalarFromDraftText(ctx.text);
    if (scale !== null && Number.isFinite(scale) && scale > 0) {
      const nextStep =
        session.options.specifyRotationOnScreen === "true"
          ? "insert-specify-rotation"
          : session.options.pendingAttribTags
            ? "insert-collect-attribs"
            : null;
      if (nextStep === null) {
        return finalizeInsertSession(session, ctx.activeSpaceId, {
          scaleX: scale,
        });
      }
      return {
        nextSession: {
          ...session,
          step: nextStep,
          options: {
            ...session.options,
            scaleX: String(scale),
          },
        },
      };
    }
    return null;
  },
  "insert-specify-rotation": (session, ctx) => {
    const rotation =
      ctx.text === "" ? 0 : (extractScalarFromDraftText(ctx.text) ?? NaN);
    if (Number.isFinite(rotation)) {
      if (!session.options.pendingAttribTags) {
        return finalizeInsertSession(session, ctx.activeSpaceId, {
          rotation,
        });
      }
      return {
        nextSession: {
          ...session,
          step: "insert-collect-attribs",
          options: {
            ...session.options,
            rotation: String(rotation),
          },
        },
      };
    }
    return null;
  },
  "insert-collect-attribs": (session, ctx) => {
    const currentTag = session.options.attribTag ?? "";
    const stored = {
      ...session.options,
      [`attribValue_${currentTag}`]: ctx.text,
    };
    let pending: string[] = [];
    try {
      pending = JSON.parse(session.options.pendingAttribTags ?? "[]");
    } catch {
      pending = [];
    }
    const next = pending.shift();
    if (next) {
      return {
        nextSession: {
          ...session,
          options: {
            ...stored,
            attribTag: next,
            attribDefault: stored[`attribDefault_${next}`] ?? "",
            pendingAttribTags: JSON.stringify(pending),
          },
        },
      };
    }
    return finalizeInsertSession(
      { ...session, options: stored },
      ctx.activeSpaceId,
      {},
    );
  },

  "hatch-pattern": (session, ctx) => ({
    nextSession: {
      ...session,
      step: "hatch-mode-prompt",
      options: {
        ...session.options,
        hatchPattern: ctx.text || "ANSI31",
        hatchScale: session.options.hatchScale ?? "1",
      },
    },
  }),
  "hatch-mode-prompt": (session, ctx) => {
    const upper = ctx.normalizedText;
    const wantsRect =
      upper === "S" ||
      upper === "SELECT" ||
      upper === "О" ||
      upper === "ОБЪЕКТЫ" ||
      upper === "R" ||
      upper === "RECT" ||
      upper === "RECTANGLE";
    const wantsInternal =
      ctx.text === "" ||
      upper === "I" ||
      upper === "P" ||
      upper === "PICK" ||
      upper === "INTERNAL" ||
      upper === "В" ||
      upper === "ВНУТРЕННЯЯ";
    if (wantsRect) {
      return {
        nextSession: {
          ...session,
          step: "hatch-first-corner",
          hoverPoint: null,
        },
      };
    }
    if (wantsInternal) {
      return {
        nextSession: {
          ...session,
          step: "hatch-pick-internal-point",
          hoverPoint: null,
          options: {
            ...session.options,
            hatchSeedPoints: "",
            hatchBoundaryPaths: "",
          },
        },
      };
    }
    return { nextSession: session };
  },

  "text-height": (session, ctx) => {
    const height = ctx.text === "" ? 2.5 : extractScalarFromDraftText(ctx.text);
    if (height === null || !Number.isFinite(height) || height <= 0) {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "text-rotation" as DraftStep,
        options: { ...session.options, height: String(height) },
      },
    };
  },
  "text-rotation": (session, ctx) => {
    const rotation =
      ctx.text === "" ? 0 : (extractScalarFromDraftText(ctx.text) ?? NaN);
    if (!Number.isFinite(rotation)) {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "text-content" as DraftStep,
        options: { ...session.options, rotation: String(rotation) },
      },
    };
  },
  "text-content": (session, ctx) => {
    const textInsertPt = session.points[0];
    const textHeight = parseFloat(session.options.height || "2.5") || 2.5;
    const textRotation = parseFloat(session.options.rotation || "0") || 0;
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-text",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          insertionPoint: { x: textInsertPt.x, y: textInsertPt.y },
          height: textHeight,
          contents: ctx.text || "Text",
          rotation: textRotation,
          styleName: "Standard",
        },
        summary: t("summary.text_created"),
        commandText: "TEXT",
      },
    };
  },
  "mtext-content": (session, ctx) => {
    const mtextPt0 = session.points[0];
    const mtextPt1 = session.points[1] ?? session.points[0];
    const mtextWidth = Math.max(48, Math.abs(mtextPt1.x - mtextPt0.x));
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-mtext",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          insertionPoint: { x: mtextPt0.x, y: mtextPt0.y },
          nominalHeight: 24,
          referenceWidth: mtextWidth,
          contents: ctx.text || "MText",
          styleName: "Standard",
        },
        summary: t("summary.mtext_created"),
        commandText: "MTEXT",
      },
    };
  },
  "mleader-content": (session, ctx) => ({
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/create-multileader",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        arrowPoint: { x: session.points[0].x, y: session.points[0].y },
        landingPoint: { x: session.points[1].x, y: session.points[1].y },
        textContents: ctx.text || "Leader",
      },
      summary: t("summary.mleader_created"),
      commandText: "MLEADER",
    },
  }),

  "bedit-prompt-name": (session, ctx) => {
    if (ctx.text === "") {
      return { nextSession: null };
    }
    if (ctx.normalizedText === "?") {
      return {
        nextSession: {
          ...session,
          options: { ...session.options, showBlockListHint: "true" },
        },
      };
    }
    if (isAnonymousBlockName(ctx.text)) {
      return {
        nextSession: null,
        commit: {
          summary: t("toast.bedit.cannot_anonymous", { name: ctx.text }),
          commandText: "BEDIT",
          feedOnly: true,
        },
      };
    }
    let knownBlocks: Set<string>;
    try {
      const parsed: unknown = JSON.parse(session.options.blockNames ?? "[]");
      knownBlocks = new Set(
        Array.isArray(parsed)
          ? parsed.filter((name): name is string => typeof name === "string")
          : [],
      );
    } catch {
      knownBlocks = new Set();
    }
    if (knownBlocks.has(ctx.text)) {
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "space/enter-block-edit",
            source: "scene",
            blockName: ctx.text,
            createIfMissing: false,
          },
          summary: t("summary.bedit_entered", { name: ctx.text }),
          commandText: "BEDIT",
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "bedit-confirm-create",
        options: { ...session.options, blockName: ctx.text },
      },
    };
  },
  "bedit-confirm-create": (session, ctx) => {
    if (ctx.normalizedText.startsWith("Y") || ctx.normalizedText === "ДА") {
      const name = session.options.blockName ?? "";
      if (!name) return { nextSession: null };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "space/enter-block-edit",
            source: "scene",
            blockName: name,
            createIfMissing: true,
          },
          summary: t("summary.bedit_created", { name }),
          commandText: "BEDIT",
        },
      };
    }
    if (ctx.normalizedText.startsWith("N") || ctx.normalizedText === "НЕТ") {
      return { nextSession: null };
    }
    return { nextSession: session };
  },
  "bclose-confirm": (session, ctx) => {
    if (ctx.normalizedText.startsWith("Y") || ctx.normalizedText === "ДА") {
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "space/exit-block-edit",
            source: "scene",
            action: "save",
          },
          summary: t("summary.bclose_saved", {
            name: session.options.blockName ?? "",
          }),
          commandText: "BCLOSE",
        },
      };
    }
    if (ctx.normalizedText.startsWith("N") || ctx.normalizedText === "НЕТ") {
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "space/exit-block-edit",
            source: "scene",
            action: "discard",
          },
          summary: t("summary.bclose_discarded", {
            name: session.options.blockName ?? "",
          }),
          commandText: "BCLOSE",
        },
      };
    }
    if (
      ctx.normalizedText.startsWith("C") ||
      ctx.normalizedText === "ОТМЕНА" ||
      ctx.text === ""
    ) {
      return { nextSession: null };
    }
    return { nextSession: session };
  },
  "attdef-dialog": (session, ctx) => {
    if (ctx.text === "") return { nextSession: null };
    return { nextSession: session };
  },
  "ddatte-dialog": (session, ctx) => {
    if (ctx.text === "") return { nextSession: null };
    return { nextSession: session };
  },
  "eattedit-dialog": (session, ctx) => {
    if (ctx.text === "") return { nextSession: null };
    return { nextSession: session };
  },
  "mleaderedit-options": (session, ctx) => {
    if (ctx.normalizedText.startsWith("A")) {
      return {
        nextSession: {
          ...session,
          step: "mleaderedit-add-leader-arrow",
        },
      };
    }
    if (ctx.normalizedText.startsWith("R")) {
      return {
        nextSession: {
          ...session,
          step: "mleaderedit-remove-pick",
        },
      };
    }
    return { nextSession: session };
  },
  "mleaderedit-select": (session, ctx) => {
    if (!ctx.text) return { nextSession: session };
    const target = ctx.existingEntities.find((e) => e.id === ctx.text);
    if (!target || target.type !== "Multileader") {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "mleaderedit-options",
        options: { ...session.options, targetEntityId: target.id },
      },
    };
  },
  "mleaderedit-remove-pick": (session, ctx) => {
    const targetEntityId = session.options.targetEntityId;
    if (!targetEntityId) {
      return { nextSession: null };
    }
    const leaderIndex = ctx.text === "" ? 0 : Number(ctx.text);
    if (!Number.isFinite(leaderIndex) || leaderIndex < 0) {
      return { nextSession: session };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/remove-leader-from-multileader",
          source: "scene",
          entityId: targetEntityId,
          leaderIndex: Math.floor(leaderIndex),
        },
        summary: t("summary.mleader_leader_removed"),
        commandText: "MLEADEREDIT",
      },
    };
  },

  "block-select-objects": (session, ctx) => {
    if (ctx.text !== "") return null;
    const raw = session.options.targetEntityIds;
    let entityIds: string[] = [];
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          entityIds = parsed.filter(
            (id): id is string => typeof id === "string",
          );
        }
      } catch {
        entityIds = [raw];
      }
    }
    if (entityIds.length === 0) {
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "block-base-point",
        hoverPoint: null,
      },
    };
  },

  // ── REVCLOUD (REVCLOUD-MODES) ───────────────────────────────────
  // First prompt: "Specify first point or [Arc length/Object/
  // Rectangular/Polygonal/Freehand/Style/Modify] <Object>:". Empty
  // Enter takes the <Object> default; R/P/F set the PERSISTED creation
  // method (AutoCAD REVCLOUDCREATEMODE) and stay on the first prompt;
  // A and S detour into the arc-length / style sub-prompts. Non-keyword
  // text falls through to the coordinate parser (first point pick).
  "revcloud-first-corner": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult | null => {
    if (ctx.text === "") {
      return {
        nextSession: {
          ...session,
          step: "revcloud-object-pick",
          hoverPoint: null,
        },
      };
    }
    const rcKeyword = matchRevcloudFirstPromptKeyword(ctx.normalizedText);
    // `null` = not handled — fall through to the coordinate parser so
    // non-keyword text can be interpreted as the first-point pick.
    if (!rcKeyword) return null;
    switch (rcKeyword) {
      case "arc-length":
        return {
          nextSession: {
            ...session,
            step: "revcloud-arc-length",
            hoverPoint: null,
          },
        };
      case "style":
        return {
          nextSession: { ...session, step: "revcloud-style", hoverPoint: null },
        };
      case "object":
        // One-shot option — AutoCAD's REVCLOUDCREATEMODE does not
        // include Object, so the persisted default is left untouched.
        return {
          nextSession: {
            ...session,
            step: "revcloud-object-pick",
            hoverPoint: null,
          },
        };
      case "rectangular":
      case "polygonal":
      case "freehand": {
        const rcMode =
          rcKeyword === "rectangular"
            ? "Rectangular"
            : rcKeyword === "polygonal"
              ? "Polygonal"
              : "Freehand";
        setRevcloudDefaultMode(rcMode);
        return {
          nextSession: {
            ...session,
            hoverPoint: null,
            options: { ...session.options, revcloudMode: rcMode },
          },
        };
      }
      case "modify":
        // REVCLOUD Modify (redraw a side of an existing cloud) is owned
        // by the REVCLOUD-PROPS-EDITOR slice — surface the rejection
        // instead of silently ignoring the keyword.
        return {
          nextSession: session,
          commit: {
            feedOnly: true,
            summary: t("prompt.revcloud.modify_unsupported"),
            commandText: "REVCLOUD",
          },
        };
    }
  },
  // "Specify approximate length of arc <current>:" — a single value
  // (AutoCAD 2021+ REVCLOUDAPPROXARCLEN behaviour; the legacy min/max
  // pair with REVCLOUDARCVARIANCE is owned by REVCLOUD-SYSVARS).
  "revcloud-arc-length": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult => {
    if (ctx.text === "") {
      return {
        nextSession: {
          ...session,
          step: "revcloud-first-corner",
          hoverPoint: null,
        },
      };
    }
    const rcLen = extractScalarFromDraftText(ctx.text);
    if (rcLen === null || !Number.isFinite(rcLen) || rcLen <= 0) {
      return { nextSession: session };
    }
    setRevcloudDefaultArcLength(rcLen);
    return {
      nextSession: {
        ...session,
        step: "revcloud-first-corner",
        hoverPoint: null,
        options: { ...session.options, revcloudArcLength: String(rcLen) },
      },
    };
  },
  // "Select arc style [Normal/Calligraphy] <Normal>:".
  "revcloud-style": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult => {
    if (ctx.text === "") {
      return {
        nextSession: {
          ...session,
          step: "revcloud-first-corner",
          hoverPoint: null,
        },
      };
    }
    const rcStyle = matchRevcloudStyleKeyword(ctx.normalizedText);
    if (!rcStyle) {
      return { nextSession: session };
    }
    setRevcloudDefaultStyle(rcStyle);
    return {
      nextSession: {
        ...session,
        step: "revcloud-first-corner",
        hoverPoint: null,
        options: { ...session.options, revcloudStyle: rcStyle },
      },
    };
  },
  // Polygonal vertex loop — empty Enter closes the polygon (>= 3
  // vertices); fewer picks end the command without creating anything
  // (AutoCAD aborts a 2-point polygonal cloud). Typed coords fall
  // through to the point parser.
  "revcloud-polygonal-next": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult | null => {
    if (ctx.text !== "") return null;
    if (session.points.length < 3) {
      return { nextSession: null };
    }
    return commitGeometry(
      {
        type: "geometry/create-revcloud",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        creationMode: "Polygonal",
        vertices: session.points.map((pt) => ({ x: pt.x, y: pt.y })),
        arcChordLength: revcloudSessionArcLength(session),
        revisionCloudStyle: revcloudSessionStyle(session),
        reverseDirection: false,
      },
      t("summary.revcloud_created"),
      "REVCLOUD",
    );
  },
  // Freehand — Enter closes the sampled path early (the primary close
  // is the hover sampler returning to the start point).
  "revcloud-freehand-path": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult | null => {
    if (ctx.text !== "") return null;
    if (session.points.length < 3) {
      return { nextSession: null };
    }
    return commitRevcloudFreehand(session, ctx.activeSpaceId);
  },
  // Object pick via typed entity id (mirrors mleaderedit-select). The
  // typed candidate is validated AT INPUT TIME against the typed
  // geometry entities (the preview list has no geometric primitives):
  // the same closed-outline tracer the geometry reducer runs rejects a
  // Line / open polyline RIGHT HERE — without this gate the session
  // advanced to the Reverse prompt and the reducer later no-op'd the
  // conversion (dead-end UX). AutoCAD rejects the selection with "The
  // selected object is not a valid closed object" and re-prompts.
  "revcloud-object-pick": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult => {
    if (!ctx.text) return { nextSession: session };
    const rcTarget = ctx.geometryEntities.find((e) => e.id === ctx.text);
    if (!rcTarget) {
      return { nextSession: session };
    }
    // Validity does not depend on the chord — use the shared clamp floor
    // (same fine chord the pick hit-test traces with).
    if (!revisionCloudOutlineFromSource(rcTarget, MIN_REVCLOUD_ARC_CHORD)) {
      return {
        nextSession: session,
        commit: {
          feedOnly: true,
          summary: t("prompt.revcloud.invalid_object"),
          commandText: "REVCLOUD",
        },
      };
    }
    return {
      nextSession: {
        ...session,
        step: "revcloud-object-reverse",
        hoverPoint: null,
        options: { ...session.options, revcloudSourceEntityId: rcTarget.id },
      },
    };
  },
  // "Reverse direction [Yes/No] <No>:" — commits the Object-mode
  // conversion (the reducer REPLACES the source entity with the cloud).
  "revcloud-object-reverse": (
    session: DraftSession,
    ctx: RevcloudTextCtx,
  ): RevcloudAdvanceResult => {
    const rcReverse = matchRevcloudReverseKeyword(ctx.normalizedText);
    if (rcReverse === null) {
      return { nextSession: session };
    }
    const rcSourceId = session.options.revcloudSourceEntityId;
    if (!rcSourceId) {
      return { nextSession: null };
    }
    return commitGeometry(
      {
        type: "geometry/create-revcloud",
        source: "scene",
        spaceId: ctx.activeSpaceId,
        creationMode: "Object",
        sourceEntityId: rcSourceId,
        arcChordLength: revcloudSessionArcLength(session),
        revisionCloudStyle: revcloudSessionStyle(session),
        reverseDirection: rcReverse,
      },
      t("summary.revcloud_created"),
      "REVCLOUD",
    );
  },

  "viewport-first-corner": (_session, ctx) => {
    // 4 keyword variants (2 / 3 / 4 / FIT) — split a paper-space layout
    // into pre-positioned viewports. Empty Enter falls through (next
    // layer attempts point-parser → first-corner click flow).
    if (ctx.normalizedText === "2") {
      const m = 40,
        mx = 800;
      const mvExt = modelExtentFromEntities(ctx.existingEntities);
      const mvExtPayload = mvExt ? { modelExtent: mvExt } : {};
      return {
        nextSession: null,
        commit: {
          geometryCommands: [
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: m, y: m },
              corner2: { x: mx - 10, y: 880 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: mx + 10, y: m },
              corner2: { x: 1560, y: 880 },
              ...mvExtPayload,
            },
          ],
          summary: t("summary.viewport_created") + " (2)",
          commandText: "MVIEW",
        },
      };
    }
    if (ctx.normalizedText === "3") {
      const m = 40,
        mx = 800,
        my = 460;
      const mvExt = modelExtentFromEntities(ctx.existingEntities);
      const mvExtPayload = mvExt ? { modelExtent: mvExt } : {};
      return {
        nextSession: null,
        commit: {
          geometryCommands: [
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: m, y: m },
              corner2: { x: mx - 10, y: 880 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: mx + 10, y: m },
              corner2: { x: 1560, y: my - 10 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: mx + 10, y: my + 10 },
              corner2: { x: 1560, y: 880 },
              ...mvExtPayload,
            },
          ],
          summary: t("summary.viewport_created") + " (3)",
          commandText: "MVIEW",
        },
      };
    }
    if (ctx.normalizedText === "4") {
      const m = 40,
        mx = 800,
        my = 460;
      const mvExt = modelExtentFromEntities(ctx.existingEntities);
      const mvExtPayload = mvExt ? { modelExtent: mvExt } : {};
      return {
        nextSession: null,
        commit: {
          geometryCommands: [
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: m, y: m },
              corner2: { x: mx - 10, y: my - 10 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: mx + 10, y: m },
              corner2: { x: 1560, y: my - 10 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: m, y: my + 10 },
              corner2: { x: mx - 10, y: 880 },
              ...mvExtPayload,
            },
            {
              type: "geometry/create-viewport",
              source: "scene",
              spaceId: ctx.activeSpaceId,
              corner1: { x: mx + 10, y: my + 10 },
              corner2: { x: 1560, y: 880 },
              ...mvExtPayload,
            },
          ],
          summary: t("summary.viewport_created") + " (4)",
          commandText: "MVIEW",
        },
      };
    }
    if (ctx.normalizedText === "FIT") {
      const mvExt = modelExtentFromEntities(ctx.existingEntities);
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-viewport",
            source: "scene",
            spaceId: ctx.activeSpaceId,
            corner1: { x: 80, y: 80 },
            corner2: { x: 1520, y: 880 },
            ...(mvExt ? { modelExtent: mvExt } : {}),
          },
          summary: t("summary.viewport_created_fit"),
          commandText: "MVIEW",
        },
      };
    }
    // Unknown / empty / non-keyword text: fall through so the
    // point-parser can attempt to interpret it as a first-corner pick.
    return null;
  },

  "hatch-pick-additional": (session, ctx) => {
    if (ctx.text !== "") return null;
    const seedJson = session.options.hatchSeedPoints ?? "[]";
    const pathsJson = session.options.hatchBoundaryPaths ?? "[]";
    let seedPoints: Array<{ x: number; y: number }> = [];
    let boundaryPaths: Array<{
      pathTypeFlag: number;
      edges: Array<Record<string, unknown>>;
    }> = [];
    try {
      seedPoints = JSON.parse(seedJson);
      boundaryPaths = JSON.parse(pathsJson);
    } catch {
      return { nextSession: null };
    }
    if (boundaryPaths.length === 0) {
      return { nextSession: null };
    }
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-hatch",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          boundaryPaths: boundaryPaths as never,
          seedPoints,
          patternName: session.options.hatchPattern ?? "ANSI31",
          patternScale: Number(session.options.hatchScale ?? "1") || 1,
          patternAngle: 0,
        },
        summary: t("summary.hatch_created"),
        commandText: "HATCH",
      },
    };
  },

  "hatch-ready": (session, ctx) => {
    if (ctx.text !== "") return null;
    const hatchPt0 = session.points[0];
    const hatchPt1 = session.points[1] ?? session.points[0];
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-hatch",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          boundaryCorner1: { x: hatchPt0.x, y: hatchPt0.y },
          boundaryCorner2: { x: hatchPt1.x, y: hatchPt1.y },
          patternName: session.options.hatchPattern ?? "ANSI31",
          patternScale: Number(session.options.hatchScale ?? "1") || 1,
          patternAngle: 0,
        },
        summary: t("summary.hatch_created"),
        commandText: "HATCH",
      },
    };
  },

  "viewport-ready": (session, ctx) => {
    if (ctx.text !== "") return null;
    const vpPt0 = session.points[0];
    const vpPt1 = session.points[1] ?? session.points[0];
    const vpReadyExt = modelExtentFromEntities(ctx.existingEntities);
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-viewport",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          corner1: { x: vpPt0.x, y: vpPt0.y },
          corner2: { x: vpPt1.x, y: vpPt1.y },
          ...(vpReadyExt ? { modelExtent: vpReadyExt } : {}),
        },
        summary: t("summary.viewport_created"),
        commandText: "MVIEW",
      },
    };
  },

  "spline-fit-next": (session, ctx) => {
    if (ctx.text !== "") return null;
    if (session.points.length < 2) {
      return { nextSession: session };
    }
    const fitPoints = session.points.map((p) => ({ x: p.x, y: p.y, z: 0 }));
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-spline",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          degree: 3,
          closed: false,
          rational: false,
          fitPoints,
          controlPoints: [],
          knots: [],
          weights: [],
        },
        summary: t("summary.spline_created"),
        commandText: "SPLINE",
      },
    };
  },

  "revcloud-ready": (session, ctx) => {
    if (ctx.text !== "") return null;
    const rcPt0 = session.points[0];
    const rcPt1 = session.points[1] ?? session.points[0];
    // Phase 5 parity note: legacy switch routed `nextSession` through
    // `startDraftSession("REVCLOUD", ...)` to keep the session alive
    // for repeated commits. That symbol was never imported into
    // draftStateMachine.ts (TS2304 in the typecheck baseline), so the
    // legacy path crashed at runtime. We end the session cleanly so
    // the user can re-fire REVCLOUD via the command line / palette.
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-revcloud",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          creationMode: "Rectangular",
          corner1: { x: rcPt0.x, y: rcPt0.y },
          corner2: { x: rcPt1.x, y: rcPt1.y },
          // REVCLOUD-MODES — consume the persisted Arc-length / Style
          // keyword results (previously a hardcoded arcChordLength: 1).
          arcChordLength: revcloudSessionArcLength(session),
          revisionCloudStyle: revcloudSessionStyle(session),
          reverseDirection: false,
        },
        summary: t("summary.revcloud_created"),
        commandText: "REVCLOUD",
      },
    };
  },
  "wipeout-ready": (session, ctx) => {
    if (ctx.text !== "") return null;
    const wpPt0 = session.points[0];
    const wpPt1 = session.points[1] ?? session.points[0];
    const wpVertices = [
      { x: wpPt0.x, y: wpPt0.y },
      { x: wpPt1.x, y: wpPt0.y },
      { x: wpPt1.x, y: wpPt1.y },
      { x: wpPt0.x, y: wpPt1.y },
    ];
    // Phase 5 parity note: same as revcloud-ready above — legacy used
    // `startDraftSession("WIPEOUT", ...)` (TS2304 crash). We close
    // cleanly instead.
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/create-wipeout",
          source: "scene",
          spaceId: ctx.activeSpaceId,
          vertices: wpVertices,
          frame: true,
        },
        summary: t("summary.wipeout_created"),
        commandText: "WIPEOUT",
      },
    };
  },

  // ── BLOCK AUTHORING TEXT (Phase 5.3.e sub-machine collapse) ──
  // Phase K.3 select-objects steps respond to Enter via the text
  // dispatcher; the wrapper delegates verbatim to
  // `advanceBlockAuthoringWithText`.
  ...Object.fromEntries(BLOCK_AUTHORING_TEXT_ENTRIES),

  // ── MODIFY TEXT (Phase 5.3.f sub-machine collapse) ──
  // Modify text dispatch (Enter to advance from select / scalar
  // keyword input for rotate angle / scale factor / offset
  // distance / etc.). Wrapper delegates to
  // `advanceModifySessionWithText`.
  ...Object.fromEntries(MODIFY_TEXT_ENTRIES),

  // ── VPCLIP-INTERACTIVE (W2.6) ──
  // Enter-cancels at "Select viewport to clip:" (AutoCAD parity, mirrors
  // OFFSET's `offset-select-entity` Enter-exit). See `vpclipRegistry.ts`.
  ...Object.fromEntries(VPCLIP_TEXT_ENTRIES),
};

/**
 * Steps that share the same body. ddatte-select and eattedit-select share
 * the SELECT body; both routed via this helper because Partial<Record>
 * cannot have multiple keys point at the same closure literal cleanly.
 */
function attribSelectBody(
  session: DraftSession,
  ctx: AdvanceWithTextContext,
): AdvanceResult {
  if (ctx.text === "") return { nextSession: null };
  const target = ctx.existingEntities.find((e) => e.id === ctx.text);
  if (!target || target.type !== "Block") {
    return { nextSession: session };
  }
  const nextStep: DraftStep =
    session.step === "ddatte-select" ? "ddatte-dialog" : "eattedit-dialog";
  return {
    nextSession: {
      ...session,
      step: nextStep,
      options: { ...session.options, targetEntityId: target.id },
    },
  };
}

// Wire shared bodies into the table here to keep entries terse above.
(ADVANCE_WITH_TEXT as Record<DraftStep, AdvanceWithTextFn>)["ddatte-select"] =
  attribSelectBody;
(ADVANCE_WITH_TEXT as Record<DraftStep, AdvanceWithTextFn>)[
  "eattedit-select"
] = attribSelectBody;

// ─────────────────────────────────────────────────────────────────────
// Entry-point lookup helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Lookup helper for the point dispatcher. Returns `null` when the step
 * has no per-step handler — caller falls through to the default
 * `{ nextSession: session }` branch.
 */
export function advanceWithPointFromRegistry(
  session: DraftSession,
  point: DraftPoint,
  ctx: AdvanceWithPointContext,
): AdvanceResult | null {
  const fn = ADVANCE_WITH_POINT[session.step];
  if (!fn) return null;
  return fn(session, point, ctx);
}

/**
 * Lookup helper for the text dispatcher. Returns `null` when the step
 * has no per-step handler OR when the per-step handler explicitly
 * decided to fall through (e.g. polygon-radius receiving non-numeric
 * text — falls through so the trailing point-parser can try).
 */
export function advanceWithTextFromRegistry(
  session: DraftSession,
  ctx: AdvanceWithTextContext,
): AdvanceResult | null {
  const fn = ADVANCE_WITH_TEXT[session.step];
  if (!fn) return null;
  return fn(session, ctx);
}

/**
 * Phase 5.3.h — unified-shape accessor. Returns both the point and
 * text handlers (if any) for a given step as a single record. Built
 * on top of the existing parallel `ADVANCE_WITH_POINT` /
 * `ADVANCE_WITH_TEXT` maps — no structural rewrite. New consumers
 * (e.g. a future dispatcher that wants to inspect both branches in
 * one call) can use this shape; legacy callers keep using the
 * `*FromRegistry` lookups above.
 *
 * The full structural unification (single
 * `ADVANCE: Record<DraftStep, { point?, text? }>` map replacing the
 * two parallel maps) was the original §4 5.3.h scope, but the cost
 * (~5 000 LOC of object-literal rewrite across two ~2 500-LOC maps)
 * exceeds the win (a marginal cognitive-load improvement). The
 * accessor below provides the unified view for new code at near-
 * zero risk.
 */
export interface AdvanceHandlers {
  readonly point?: AdvanceWithPointFn;
  readonly text?: AdvanceWithTextFn;
}

export function getAdvanceHandlers(step: DraftStep): AdvanceHandlers {
  return {
    point: ADVANCE_WITH_POINT[step],
    text: ADVANCE_WITH_TEXT[step],
  };
}
